/**
 * BATCH 24 PART 5 — Workflows 41-50: Finance & RevOps
 * 10 elite production-standard workflow templates
 * Services: QuickBooks, Stripe, HubSpot, Google Sheets, Slack, OpenAI
 *
 * Production enhancements in every workflow:
 * 1. Case stamping (node 2) — unique case_id + timestamps
 * 2. aiNodeV2 — confidence_score + evidence_summary + needs_human_review
 * 3. confidenceGate — routes high-confidence auto vs low-confidence human review
 * 4. verifyNode — read-back confirmation of executed actions
 * 5. exceptionNode / sheetsNode — structured logging or error handling
 */

// ============ BASE HELPERS ============

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}

// ============ ENHANCED HELPERS — V2 FOR BATCH 24 ============

function aiNodeV2(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  const enhancedSystem =
    systemPrompt +
    " Also include in your JSON response: confidence_score (0-100 integer rating your certainty), evidence_summary (one sentence citing the key data points you relied on), and needs_human_review (boolean, true if you are uncertain or the case is unusual).";
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${enhancedSystem}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "choices[0].message.content.confidence_score",
            customName: "ai_confidence",
            type: "number",
          },
          {
            name: "choices[0].message.content.evidence_summary",
            customName: "ai_evidence",
            type: "string",
          },
          {
            name: "choices[0].message.content.needs_human_review",
            customName: "ai_needs_review",
            type: "string",
          },
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}

function caseStampNode(
  id: string,
  label: string,
  y: number,
  workflowName: string,
  x = 80,
): any {
  return n(
    id,
    "runCode",
    label,
    y,
    {
      code: `// Stamp case with unique ID, timestamp, and workflow version
const ts = Date.now();
const rand = Math.random().toString(36).substring(2, 8);
output.case_id = 'case_' + ts + '_' + rand;
output.workflow_name = '${workflowName}';
output.workflow_version = '1.0';
output.started_at = new Date(ts).toISOString();
output.source_channel = variables.source_channel || 'webhook';
output.status = 'intake_complete';`,
    },
    x,
  );
}

function confidenceGate(
  id: string,
  label: string,
  y: number,
  threshold = "75",
  x = 80,
): any {
  return n(
    id,
    "condition",
    label,
    y,
    {
      conditionData: [
        {
          variable: "ai_confidence",
          value: threshold,
          comparison_operator: "GREATER THAN",
          logical_operator: "AND",
        },
      ],
    },
    x,
  );
}

function conditionNode(
  id: string,
  label: string,
  y: number,
  variable: string,
  operator: string,
  value: string,
  x = 80,
): any {
  return n(
    id,
    "condition",
    label,
    y,
    {
      conditionData: [
        {
          variable,
          value,
          comparison_operator: operator,
          logical_operator: "AND",
        },
      ],
    },
    x,
  );
}

function approvalNode(
  id: string,
  label: string,
  y: number,
  message: string,
  timeoutHours = 24,
  x = 80,
): any {
  return n(
    id,
    "approvalGate",
    label,
    y,
    {
      approvalGateData: {
        approvers: [],
        message,
        channel: "email",
        timeoutHours,
      },
    },
    x,
  );
}

function verifyNode(
  id: string,
  label: string,
  y: number,
  method: string,
  url: string,
  headers: Array<{ key: string; value: string }>,
  expectedVars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method,
        url,
        headers,
        body: [],
        variables: [
          ...expectedVars,
          { name: "status_code", customName: "verify_status", type: "number" },
        ],
      },
    },
    x,
  );
}

function exceptionNode(id: string, label: string, y: number, x = 80): any {
  return n(
    id,
    "runCode",
    label,
    y,
    {
      code: `// Exception handler: categorize, log, and route
const errorType = variables.error_message || variables.verify_status || 'unknown';
const isTransient = ['timeout', '429', '503', '502'].some(e => String(errorType).includes(e));
const retryCount = parseInt(variables.retry_count || '0');
output.exception_type = isTransient ? 'transient' : 'permanent';
output.should_retry = isTransient && retryCount < 3 ? 'yes' : 'no';
output.retry_count = String(retryCount + 1);
output.exception_summary = 'Error: ' + errorType + ' | Type: ' + output.exception_type + ' | Retry: ' + output.should_retry;
output.escalate_to_human = (!isTransient || retryCount >= 3) ? 'yes' : 'no';
output.logged_at = new Date().toISOString();`,
    },
    x,
  );
}

function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}

function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}

function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}

// ============ WORKFLOWS 41-50: FINANCE & REVOPS ============

export const ELITE_BATCH24_PART5: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ── 41. Elite: Invoice Processing + Approval ──
  "Elite: Invoice Processing + Approval": {
    nodes: [
      trigger("Email: Invoice received from vendor", "email", {
        source: "Email",
        event: "invoice.received",
        howItStarts:
          "Fires when a vendor invoice email arrives in the AP inbox, forwarded via email-to-webhook integration.",
        setup:
          "Configure email forwarding from AP inbox to the webhook endpoint. Ensure PDF attachments and sender metadata are included in the payload.",
        inputFields: [
          {
            name: "sender_email",
            type: "string",
            example: "billing@acmevendor.com",
          },
          {
            name: "subject",
            type: "string",
            example: "Invoice #INV-2024-0891",
          },
          {
            name: "attachment_url",
            type: "string",
            example: "https://storage.example.com/inv-2024-0891.pdf",
          },
        ],
      }),
      caseStampNode(
        "2",
        "Stamp case: Invoice processing",
        200,
        "Invoice Processing + Approval",
      ),
      aiNodeV2(
        "3",
        "OpenAI: OCR + extract invoice fields",
        360,
        "You are an AP automation assistant. Extract structured invoice data from the provided email and attachment content. Return JSON with vendor_name, invoice_number, invoice_date, due_date, line_items_summary, subtotal, tax_amount, total_amount, currency, and po_number if present.",
        "Invoice email from {sender_email}. Subject: {subject}. Attachment URL: {attachment_url}. Email body: {{{webhook_body}}}",
        [
          { field: "vendor_name", as: "vendor_name", type: "string" },
          { field: "invoice_number", as: "invoice_number", type: "string" },
          { field: "invoice_date", as: "invoice_date", type: "string" },
          { field: "due_date", as: "due_date", type: "string" },
          { field: "total_amount", as: "total_amount", type: "number" },
          { field: "po_number", as: "po_number", type: "string" },
          {
            field: "line_items_summary",
            as: "line_items_summary",
            type: "string",
          },
        ],
      ),
      n("4", "hitApi", "QuickBooks: Match PO and vendor record", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://quickbooks.api.intuit.com/v3/company/{realm_id}/query?query=select * from PurchaseOrder where DocNumber = '{po_number}'",
          headers: [
            { key: "Authorization", value: "Bearer {qb_token}" },
            { key: "Accept", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "QueryResponse.PurchaseOrder[0].Id",
              customName: "po_id",
              type: "string",
            },
            {
              name: "QueryResponse.PurchaseOrder[0].TotalAmt",
              customName: "po_total",
              type: "number",
            },
            {
              name: "QueryResponse.PurchaseOrder[0].VendorRef.name",
              customName: "po_vendor",
              type: "string",
            },
          ],
        },
      }),
      confidenceGate("5", "Gate: AI extraction confidence check", 680),
      n("6", "hitApi", "QuickBooks: Auto-code GL + queue payment", 840, {
        hitApiDetails: {
          method: "POST",
          url: "https://quickbooks.api.intuit.com/v3/company/{realm_id}/bill",
          headers: [
            { key: "Authorization", value: "Bearer {qb_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "VendorRef", value: '{"value":"{po_vendor}"}' },
            {
              key: "Line",
              value:
                '[{"Amount":{total_amount},"DetailType":"AccountBasedExpenseLineDetail","AccountBasedExpenseLineDetail":{"AccountRef":{"value":"auto_coded"}}}]',
            },
            { key: "DocNumber", value: "{invoice_number}" },
          ],
          variables: [
            { name: "Bill.Id", customName: "bill_id", type: "string" },
            {
              name: "Bill.Balance",
              customName: "bill_balance",
              type: "number",
            },
          ],
        },
      }),
      approvalNode(
        "7",
        "Approval: AP manager review (low confidence)",
        840,
        "Invoice #{invoice_number} from {vendor_name} for ${total_amount} needs manual review. AI confidence: {ai_confidence}%. Evidence: {ai_evidence}. PO match: {po_number}. Please verify GL coding and approve for payment.",
        48,
        400,
      ),
      verifyNode(
        "8",
        "Verify: Invoice booked in QuickBooks",
        1000,
        "GET",
        "https://quickbooks.api.intuit.com/v3/company/{realm_id}/bill/{bill_id}",
        [
          { key: "Authorization", value: "Bearer {qb_token}" },
          { key: "Accept", value: "application/json" },
        ],
        [
          { name: "Bill.Id", customName: "verified_bill_id", type: "string" },
          {
            name: "Bill.Balance",
            customName: "verified_balance",
            type: "number",
          },
        ],
      ),
      conditionNode(
        "9",
        "Check: Invoice verification passed",
        1160,
        "verify_status",
        "EQUALS",
        "200",
      ),
      sheetsNode(
        "10",
        "Sheets: Log invoice processing outcome",
        1320,
        "InvoiceLog",
        '[["{case_id}","{invoice_number}","{vendor_name}","{total_amount}","{po_number}","{ai_confidence}","{verified_bill_id}","{started_at}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10", "output_1"),
    ],
    summary:
      "Receives vendor invoices via email, uses AI to OCR and extract fields, matches against QuickBooks POs, auto-codes high-confidence invoices for payment, routes low-confidence to AP manager approval, verifies booking, and logs outcomes to Sheets.",
  },

  // ── 42. Elite: Expense Report Validator ──
  "Elite: Expense Report Validator": {
    nodes: [
      trigger("Webhook: Expense report submitted", "webhook_app", {
        source: "ExpenseApp",
        event: "expense.submitted",
        howItStarts:
          "Fires when an employee submits an expense report through the expense management system.",
        setup:
          "Connect expense management webhook for the report.submitted event. Payload includes employee info, receipts, amounts, categories, and policy metadata.",
        inputFields: [
          {
            name: "employee_email",
            type: "string",
            example: "jane.doe@company.com",
          },
          { name: "employee_name", type: "string", example: "Jane Doe" },
          { name: "report_id", type: "string", example: "EXP-2024-0412" },
          { name: "total_amount", type: "number", example: "1247.50" },
          {
            name: "expense_items",
            type: "string",
            example:
              "[{category:'travel',amount:800},{category:'meals',amount:447.50}]",
          },
        ],
      }),
      caseStampNode(
        "2",
        "Stamp case: Expense validation",
        200,
        "Expense Report Validator",
      ),
      aiNodeV2(
        "3",
        "OpenAI: Validate policy compliance + flag violations",
        360,
        "You are a corporate expense policy auditor. Analyze the submitted expense report against standard corporate policies. Check for: duplicate receipts, over-limit categories (meals >$75/day, hotel >$250/night, flights must be economy), missing receipts, weekend expenses without justification, and suspicious patterns. Return JSON with policy_compliant (boolean), violations (comma-separated list or 'none'), risk_score (1-10), recommended_action ('auto_approve','manager_review','reject'), and summary.",
        "Employee: {employee_name} ({employee_email}). Report: {report_id}. Total: ${total_amount}. Items: {expense_items}",
        [
          { field: "policy_compliant", as: "policy_compliant", type: "string" },
          { field: "violations", as: "violations", type: "string" },
          { field: "risk_score", as: "risk_score", type: "number" },
          {
            field: "recommended_action",
            as: "recommended_action",
            type: "string",
          },
          { field: "summary", as: "validation_summary", type: "string" },
        ],
      ),
      n("4", "runCode", "Policy rules: Check amount thresholds", 520, {
        code: `// Hard policy rules check
const total = parseFloat(variables.total_amount) || 0;
const autoApproveLimit = 500;
const managerLimit = 5000;
output.under_auto_limit = total <= autoApproveLimit ? 'yes' : 'no';
output.needs_vp_approval = total > managerLimit ? 'yes' : 'no';
output.approval_tier = total <= autoApproveLimit ? 'auto' : total <= managerLimit ? 'manager' : 'vp';
output.policy_flags = variables.violations || 'none';`,
      }),
      confidenceGate("5", "Gate: AI validation confidence check", 680),
      n("6", "hitApi", "ExpenseApp: Auto-approve under threshold", 840, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.expenseapp.com/v1/reports/{report_id}/approve",
          headers: [
            { key: "Authorization", value: "Bearer {expense_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "status", value: "approved" },
            { key: "approved_by", value: "automation" },
            {
              key: "notes",
              value:
                "Auto-approved: policy compliant, under ${autoApproveLimit} threshold. AI confidence: {ai_confidence}%",
            },
          ],
          variables: [
            {
              name: "report.status",
              customName: "approval_status",
              type: "string",
            },
            {
              name: "report.approved_at",
              customName: "approved_at",
              type: "string",
            },
          ],
        },
      }),
      approvalNode(
        "7",
        "Approval: Manager review (low confidence)",
        840,
        "Expense report {report_id} from {employee_name} for ${total_amount} needs review. Violations: {violations}. Risk score: {risk_score}/10. AI confidence: {ai_confidence}%. Summary: {validation_summary}. Approval tier: {approval_tier}.",
        48,
        400,
      ),
      verifyNode(
        "8",
        "Verify: Reimbursement queued in system",
        1000,
        "GET",
        "https://api.expenseapp.com/v1/reports/{report_id}",
        [
          { key: "Authorization", value: "Bearer {expense_token}" },
          { key: "Accept", value: "application/json" },
        ],
        [
          {
            name: "report.status",
            customName: "verified_status",
            type: "string",
          },
          {
            name: "report.reimbursement_queued",
            customName: "reimbursement_queued",
            type: "string",
          },
        ],
      ),
      conditionNode(
        "9",
        "Check: Reimbursement verification passed",
        1160,
        "verify_status",
        "EQUALS",
        "200",
      ),
      sheetsNode(
        "10",
        "Sheets: Log expense validation outcome",
        1320,
        "ExpenseLog",
        '[["{case_id}","{report_id}","{employee_name}","{total_amount}","{policy_compliant}","{violations}","{ai_confidence}","{approval_tier}","{verified_status}","{started_at}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10", "output_1"),
    ],
    summary:
      "Validates employee expense reports against corporate policy using AI, auto-approves compliant reports under threshold, routes flagged or high-amount reports to manager approval, verifies reimbursement is queued, and logs all outcomes to Sheets.",
  },

  // ── 43. Elite: Revenue Recognition Prep ──
  "Elite: Revenue Recognition Prep": {
    nodes: [
      trigger("Schedule: Weekly rev-rec preparation", "schedule", {
        schedule: "Every Monday at 6:00 AM UTC",
        howItStarts:
          "Runs weekly to prepare revenue recognition journal entries from recently closed deals.",
        setup:
          "Configure the schedule trigger to run every Monday morning. Ensure HubSpot and QuickBooks API tokens are configured with appropriate scopes for deal reads and journal entry creation.",
        inputFields: [],
      }),
      caseStampNode(
        "2",
        "Stamp case: Rev-rec preparation",
        200,
        "Revenue Recognition Prep",
      ),
      aiNodeV2(
        "3",
        "OpenAI: Apply ASC 606 rev-rec rules to deals",
        360,
        "You are a revenue recognition specialist applying ASC 606 rules. Analyze each closed deal and determine: recognition_type ('point_in_time' or 'over_time'), recognition_schedule (immediate or monthly allocation), deferred_amount, recognized_amount, period_start, period_end, and any adjustments needed. Return JSON with deals_analyzed (count), total_recognized, total_deferred, journal_entries (array summary as string), and exceptions (any deals requiring manual review).",
        "Closed deals this week from HubSpot: {deals_data}. Current accounting period: {accounting_period}. Company rev-rec policy: standard SaaS with annual contracts recognized monthly.",
        [
          { field: "deals_analyzed", as: "deals_analyzed", type: "number" },
          { field: "total_recognized", as: "total_recognized", type: "number" },
          { field: "total_deferred", as: "total_deferred", type: "number" },
          { field: "journal_entries", as: "journal_entries", type: "string" },
          { field: "exceptions", as: "rev_rec_exceptions", type: "string" },
        ],
      ),
      n("4", "hitApi", "HubSpot: Fetch closed deals this period", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/deals/search",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "filterGroups",
              value:
                '[{"filters":[{"propertyName":"dealstage","operator":"EQ","value":"closedwon"},{"propertyName":"closedate","operator":"GTE","value":"{seven_days_ago}"}]}]',
            },
            {
              key: "properties",
              value:
                '["dealname","amount","closedate","deal_currency_code","pipeline","hs_object_id"]',
            },
            { key: "limit", value: "100" },
          ],
          variables: [
            { name: "total", customName: "deals_count", type: "number" },
            { name: "results", customName: "deals_data", type: "string" },
          ],
        },
      }),
      confidenceGate("5", "Gate: Rev-rec classification confidence", 680),
      n("6", "hitApi", "QuickBooks: Create journal entries", 840, {
        hitApiDetails: {
          method: "POST",
          url: "https://quickbooks.api.intuit.com/v3/company/{realm_id}/journalentry",
          headers: [
            { key: "Authorization", value: "Bearer {qb_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "Line", value: "{journal_entries}" },
            { key: "TxnDate", value: "{started_at}" },
            {
              key: "PrivateNote",
              value:
                "Auto-generated rev-rec entries. Case: {case_id}. Deals analyzed: {deals_analyzed}.",
            },
          ],
          variables: [
            { name: "JournalEntry.Id", customName: "je_id", type: "string" },
            {
              name: "JournalEntry.TotalAmt",
              customName: "je_total",
              type: "number",
            },
          ],
        },
      }),
      approvalNode(
        "7",
        "Approval: Controller review (low confidence)",
        840,
        "Rev-rec journal entries need controller review. Deals analyzed: {deals_analyzed}. Total recognized: ${total_recognized}. Total deferred: ${total_deferred}. Exceptions: {rev_rec_exceptions}. AI confidence: {ai_confidence}%.",
        72,
        400,
      ),
      verifyNode(
        "8",
        "Verify: Journal entries posted in QuickBooks",
        1000,
        "GET",
        "https://quickbooks.api.intuit.com/v3/company/{realm_id}/journalentry/{je_id}",
        [
          { key: "Authorization", value: "Bearer {qb_token}" },
          { key: "Accept", value: "application/json" },
        ],
        [
          {
            name: "JournalEntry.Id",
            customName: "verified_je_id",
            type: "string",
          },
          {
            name: "JournalEntry.TotalAmt",
            customName: "verified_je_total",
            type: "number",
          },
        ],
      ),
      conditionNode(
        "9",
        "Check: Journal entry verification passed",
        1160,
        "verify_status",
        "EQUALS",
        "200",
      ),
      sheetsNode(
        "10",
        "Sheets: Log rev-rec processing outcome",
        1320,
        "RevRecLog",
        '[["{case_id}","{deals_analyzed}","{total_recognized}","{total_deferred}","{rev_rec_exceptions}","{ai_confidence}","{verified_je_id}","{started_at}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10", "output_1"),
    ],
    summary:
      "Weekly schedule fetches closed deals from HubSpot, applies ASC 606 rev-rec rules via AI, auto-posts journal entries to QuickBooks for high-confidence classifications, routes exceptions to controller approval, verifies entries posted, and logs to Sheets.",
  },

  // ── 44. Elite: Failed Payment Recovery ──
  "Elite: Failed Payment Recovery": {
    nodes: [
      trigger("Stripe: Payment failed webhook", "webhook_app", {
        source: "Stripe",
        event: "payment_intent.payment_failed",
        howItStarts:
          "Fires when a Stripe payment attempt fails, indicating a billing issue that needs recovery action.",
        setup:
          "Configure Stripe webhook for payment_intent.payment_failed events. Ensure the payload includes customer ID, payment method details, failure code, and amount.",
        inputFields: [
          {
            name: "payment_intent_id",
            type: "string",
            example: "pi_3Abc123Def456",
          },
          { name: "customer_id", type: "string", example: "cus_Xyz789" },
          { name: "failure_code", type: "string", example: "card_declined" },
          { name: "amount", type: "number", example: "9900" },
        ],
      }),
      caseStampNode(
        "2",
        "Stamp case: Payment recovery",
        200,
        "Failed Payment Recovery",
      ),
      aiNodeV2(
        "3",
        "OpenAI: Classify failure + recommend recovery action",
        360,
        "You are a payment recovery specialist. Analyze the payment failure and recommend the best recovery action. Consider failure codes (card_declined, insufficient_funds, expired_card, processing_error, etc.), customer history, and amount. Return JSON with failure_category ('card_issue','funds_issue','technical','fraud_risk'), recommended_action ('retry_immediate','email_update_payment','wait_and_retry','escalate_to_finance','block_account'), retry_delay_hours (number), email_tone ('friendly','urgent','final_notice'), customer_risk ('low','medium','high'), and recovery_message (personalized message to send).",
        "Payment failed. Intent: {payment_intent_id}. Customer: {customer_id}. Failure code: {failure_code}. Amount: {amount} cents. Customer data: {customer_data}",
        [
          { field: "failure_category", as: "failure_category", type: "string" },
          {
            field: "recommended_action",
            as: "recommended_action",
            type: "string",
          },
          {
            field: "retry_delay_hours",
            as: "retry_delay_hours",
            type: "number",
          },
          { field: "email_tone", as: "email_tone", type: "string" },
          { field: "customer_risk", as: "customer_risk", type: "string" },
          { field: "recovery_message", as: "recovery_message", type: "string" },
        ],
      ),
      n("4", "hitApi", "Stripe: Fetch customer and payment history", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.stripe.com/v1/customers/{customer_id}",
          headers: [
            { key: "Authorization", value: "Bearer {stripe_secret_key}" },
          ],
          body: [],
          variables: [
            { name: "email", customName: "customer_email", type: "string" },
            { name: "name", customName: "customer_name", type: "string" },
            {
              name: "metadata.company",
              customName: "customer_company",
              type: "string",
            },
            {
              name: "invoice_settings.default_payment_method",
              customName: "default_pm",
              type: "string",
            },
          ],
        },
      }),
      confidenceGate("5", "Gate: Recovery action confidence check", 680),
      emailNode(
        "6",
        "Email: Send payment update request",
        840,
        "{customer_email}",
        "Action needed: Update your payment method — {customer_company}",
        "Hi {customer_name},\\n\\n{recovery_message}\\n\\nUpdate your payment method here: https://billing.yourapp.com/update?cid={customer_id}\\n\\nIf you have questions, reply to this email and our billing team will help.\\n\\nBest regards,\\nBilling Team",
      ),
      approvalNode(
        "7",
        "Approval: Finance review (low confidence)",
        840,
        "Failed payment needs finance review. Customer: {customer_name} ({customer_email}). Amount: {amount} cents. Failure: {failure_code}. Category: {failure_category}. Risk: {customer_risk}. AI recommended: {recommended_action}. Confidence: {ai_confidence}%.",
        24,
        400,
      ),
      verifyNode(
        "8",
        "Verify: Recovery email sent successfully",
        1000,
        "GET",
        "https://api.postmarkapp.com/messages/outbound?recipient={customer_email}&count=1",
        [
          { key: "X-Postmark-Server-Token", value: "{postmark_token}" },
          { key: "Accept", value: "application/json" },
        ],
        [
          {
            name: "Messages[0].Status",
            customName: "email_status",
            type: "string",
          },
          {
            name: "Messages[0].MessageID",
            customName: "email_message_id",
            type: "string",
          },
        ],
      ),
      conditionNode(
        "9",
        "Check: Email delivery verified",
        1160,
        "verify_status",
        "EQUALS",
        "200",
      ),
      exceptionNode(
        "10",
        "Exception: Handle delivery failure + retry logic",
        1320,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10", "output_2"),
    ],
    summary:
      "Catches Stripe payment failures, uses AI to classify failure type and recommend recovery action, fetches customer data, sends personalized payment update emails for high-confidence cases, routes complex failures to finance approval, verifies delivery, and handles exceptions.",
  },

  // ── 45. Elite: Deal Desk Price Exception ──
  "Elite: Deal Desk Price Exception": {
    nodes: [
      trigger("HubSpot: Deal discount exceeds 20%", "webhook_app", {
        source: "HubSpot",
        event: "deal.propertyChange",
        howItStarts:
          "Fires when a HubSpot deal's discount percentage exceeds 20%, triggering deal desk review for price exception approval.",
        setup:
          "Configure HubSpot workflow to fire webhook when deal property 'discount_percentage' changes and value > 20. Include deal ID, amount, discount, rep info, and competitor data.",
        inputFields: [
          { name: "deal_id", type: "string", example: "12345678" },
          {
            name: "deal_name",
            type: "string",
            example: "Acme Corp Enterprise",
          },
          { name: "deal_amount", type: "number", example: "120000" },
          { name: "discount_percentage", type: "number", example: "25" },
          { name: "rep_email", type: "string", example: "sarah@company.com" },
        ],
      }),
      caseStampNode(
        "2",
        "Stamp case: Deal desk exception",
        200,
        "Deal Desk Price Exception",
      ),
      aiNodeV2(
        "3",
        "OpenAI: Evaluate discount justification",
        360,
        "You are a deal desk analyst. Evaluate whether the requested discount is justified based on deal size, competitive pressure, strategic value, and company precedent. Return JSON with justification_strength ('strong','moderate','weak'), competitive_threat_level ('high','medium','low','none'), strategic_value_score (1-10), comparable_deals_summary (string describing similar past discounts), recommended_discount (the discount % you recommend), recommended_action ('approve_as_is','approve_with_conditions','counter_offer','reject'), and reasoning (2-3 sentence explanation).",
        "Deal: {deal_name}. Amount: ${deal_amount}. Requested discount: {discount_percentage}%. Rep: {rep_email}. Deal context: {deal_context}. Competitor info: {competitor_data}",
        [
          {
            field: "justification_strength",
            as: "justification_strength",
            type: "string",
          },
          {
            field: "competitive_threat_level",
            as: "competitive_threat_level",
            type: "string",
          },
          {
            field: "strategic_value_score",
            as: "strategic_value_score",
            type: "number",
          },
          {
            field: "recommended_discount",
            as: "recommended_discount",
            type: "number",
          },
          {
            field: "recommended_action",
            as: "recommended_action",
            type: "string",
          },
          { field: "reasoning", as: "deal_reasoning", type: "string" },
        ],
      ),
      n("4", "hitApi", "HubSpot: Fetch deal details + competitor data", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hubapi.com/crm/v3/objects/deals/{deal_id}?properties=dealname,amount,discount_percentage,dealstage,competitor,notes_last_updated,hubspot_owner_id",
          headers: [{ key: "Authorization", value: "Bearer {hubspot_token}" }],
          body: [],
          variables: [
            {
              name: "properties.dealname",
              customName: "deal_context",
              type: "string",
            },
            {
              name: "properties.competitor",
              customName: "competitor_data",
              type: "string",
            },
            {
              name: "properties.dealstage",
              customName: "deal_stage",
              type: "string",
            },
            {
              name: "properties.hubspot_owner_id",
              customName: "deal_owner",
              type: "string",
            },
          ],
        },
      }),
      confidenceGate("5", "Gate: Discount evaluation confidence", 680),
      n("6", "hitApi", "HubSpot: Auto-approve standard discount", 840, {
        hitApiDetails: {
          method: "PATCH",
          url: "https://api.hubapi.com/crm/v3/objects/deals/{deal_id}",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "properties",
              value:
                '{"deal_desk_status":"approved","approved_discount":"{recommended_discount}","deal_desk_notes":"Auto-approved. Case: {case_id}. Justification: {justification_strength}. AI confidence: {ai_confidence}%."}',
            },
          ],
          variables: [
            { name: "id", customName: "updated_deal_id", type: "string" },
            {
              name: "properties.deal_desk_status",
              customName: "desk_status",
              type: "string",
            },
          ],
        },
      }),
      approvalNode(
        "7",
        "Approval: VP Sales + Finance review",
        840,
        "Deal desk exception: {deal_name} requesting {discount_percentage}% discount on ${deal_amount} deal. AI recommends: {recommended_action} at {recommended_discount}%. Justification: {justification_strength}. Competitive threat: {competitive_threat_level}. Reasoning: {deal_reasoning}. AI confidence: {ai_confidence}%.",
        48,
        400,
      ),
      verifyNode(
        "8",
        "Verify: Approval recorded in HubSpot",
        1000,
        "GET",
        "https://api.hubapi.com/crm/v3/objects/deals/{deal_id}?properties=deal_desk_status,approved_discount",
        [{ key: "Authorization", value: "Bearer {hubspot_token}" }],
        [
          {
            name: "properties.deal_desk_status",
            customName: "verified_desk_status",
            type: "string",
          },
          {
            name: "properties.approved_discount",
            customName: "verified_discount",
            type: "string",
          },
        ],
      ),
      conditionNode(
        "9",
        "Check: Deal desk update verified",
        1160,
        "verify_status",
        "EQUALS",
        "200",
      ),
      sheetsNode(
        "10",
        "Sheets: Log deal desk decision",
        1320,
        "DealDeskLog",
        '[["{case_id}","{deal_name}","{deal_amount}","{discount_percentage}","{recommended_discount}","{recommended_action}","{justification_strength}","{ai_confidence}","{verified_desk_status}","{started_at}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10", "output_1"),
    ],
    summary:
      "Catches deals with discounts exceeding 20%, uses AI to evaluate justification against competitive and strategic factors, auto-approves standard exceptions with high confidence, routes complex cases to VP Sales and Finance, verifies HubSpot update, and logs decisions.",
  },

  // ── 46. Elite: Budget Variance Alert ──
  "Elite: Budget Variance Alert": {
    nodes: [
      trigger("Schedule: Daily budget variance check", "schedule", {
        schedule: "Every weekday at 8:00 AM UTC",
        howItStarts:
          "Runs daily to compare actual spend against budget and alert on significant variances.",
        setup:
          "Configure schedule trigger for weekday mornings. Ensure QuickBooks API access for profit & loss reports and budget data. Set variance threshold in workflow variables.",
        inputFields: [],
      }),
      caseStampNode(
        "2",
        "Stamp case: Budget variance analysis",
        200,
        "Budget Variance Alert",
      ),
      aiNodeV2(
        "3",
        "OpenAI: Analyze variance + identify root cause drivers",
        360,
        "You are a financial planning and analysis (FP&A) specialist. Analyze budget vs actual data and identify significant variances. For each material variance (>10% or >$5,000), determine the likely driver. Return JSON with total_budget, total_actual, total_variance_pct, material_variances (string summary of top 5 line items with variance), primary_drivers (string listing root causes), risk_level ('low','medium','high','critical'), recommended_actions (string with 2-3 bullet points), and executive_summary (2 sentence overview).",
        "Budget vs Actual data for current period: {budget_data}. Prior period comparison: {prior_period}. Department breakdowns included.",
        [
          {
            field: "total_variance_pct",
            as: "total_variance_pct",
            type: "number",
          },
          {
            field: "material_variances",
            as: "material_variances",
            type: "string",
          },
          { field: "primary_drivers", as: "primary_drivers", type: "string" },
          { field: "risk_level", as: "risk_level", type: "string" },
          {
            field: "recommended_actions",
            as: "recommended_actions",
            type: "string",
          },
          {
            field: "executive_summary",
            as: "executive_summary",
            type: "string",
          },
        ],
      ),
      n("4", "hitApi", "QuickBooks: Fetch actuals vs budget report", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://quickbooks.api.intuit.com/v3/company/{realm_id}/reports/ProfitAndLoss?start_date={period_start}&end_date={period_end}&accounting_method=Accrual",
          headers: [
            { key: "Authorization", value: "Bearer {qb_token}" },
            { key: "Accept", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "Rows", customName: "budget_data", type: "string" },
            {
              name: "Header.StartPeriod",
              customName: "period_start_actual",
              type: "string",
            },
            {
              name: "Header.EndPeriod",
              customName: "period_end_actual",
              type: "string",
            },
          ],
        },
      }),
      confidenceGate("5", "Gate: Variance analysis confidence", 680),
      slackNode(
        "6",
        "Slack: Post finance variance alert",
        840,
        ":bar_chart: *Budget Variance Alert* — Case {case_id}\\n\\n*Risk Level:* {risk_level}\\n*Total Variance:* {total_variance_pct}%\\n\\n*Executive Summary:* {executive_summary}\\n\\n*Material Variances:*\\n{material_variances}\\n\\n*Root Causes:* {primary_drivers}\\n\\n*Recommended Actions:*\\n{recommended_actions}\\n\\n_AI Confidence: {ai_confidence}%_",
      ),
      approvalNode(
        "7",
        "Approval: CFO review (low confidence)",
        840,
        "Budget variance analysis needs CFO review. Risk level: {risk_level}. Total variance: {total_variance_pct}%. Material variances: {material_variances}. Drivers: {primary_drivers}. AI confidence: {ai_confidence}%. Please review analysis accuracy and approve alert distribution.",
        24,
        400,
      ),
      verifyNode(
        "8",
        "Verify: Slack alert delivered",
        1000,
        "POST",
        "https://slack.com/api/chat.postMessage",
        [
          { key: "Authorization", value: "Bearer {slack_bot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        [
          { name: "ok", customName: "slack_delivered", type: "string" },
          { name: "ts", customName: "message_ts", type: "string" },
        ],
      ),
      conditionNode(
        "9",
        "Check: Alert delivery verified",
        1160,
        "verify_status",
        "EQUALS",
        "200",
      ),
      sheetsNode(
        "10",
        "Sheets: Log variance analysis",
        1320,
        "BudgetVarianceLog",
        '[["{case_id}","{period_start_actual}","{period_end_actual}","{total_variance_pct}","{risk_level}","{material_variances}","{primary_drivers}","{ai_confidence}","{started_at}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10", "output_1"),
    ],
    summary:
      "Daily schedule fetches actuals vs budget from QuickBooks, uses AI to identify material variances and root causes, posts Slack alerts for high-confidence analyses, routes uncertain findings to CFO review, verifies delivery, and logs to Sheets.",
  },

  // ── 47. Elite: Commission Calculator ──
  "Elite: Commission Calculator": {
    nodes: [
      trigger("HubSpot: Deal closed-won webhook", "webhook_app", {
        source: "HubSpot",
        event: "deal.closed",
        howItStarts:
          "Fires when a deal moves to closed-won stage in HubSpot, triggering commission calculation for the sales rep.",
        setup:
          "Configure HubSpot webhook for deal stage changes to closed-won. Payload must include deal amount, owner, close date, and deal type. Commission plan rules should be stored in workflow variables.",
        inputFields: [
          { name: "deal_id", type: "string", example: "9876543" },
          {
            name: "deal_name",
            type: "string",
            example: "TechCorp Annual License",
          },
          { name: "deal_amount", type: "number", example: "85000" },
          { name: "owner_email", type: "string", example: "rep@company.com" },
          { name: "deal_type", type: "string", example: "new_business" },
        ],
      }),
      caseStampNode(
        "2",
        "Stamp case: Commission calculation",
        200,
        "Commission Calculator",
      ),
      aiNodeV2(
        "3",
        "OpenAI: Compute commission + handle splits",
        360,
        "You are a sales compensation analyst. Calculate commission based on the deal details and standard plan rules: new business = 10% base rate, expansion = 7%, renewal = 4%. Apply accelerators: 1.25x for deals >$100K, 1.5x for deals >$250K. Handle multi-rep splits if applicable. Return JSON with base_rate_pct, accelerator_multiplier, gross_commission, split_details (string describing any splits or 'sole_rep'), net_commission_per_rep (the primary rep amount), quota_credit, ytd_attainment_impact, and calculation_breakdown (step-by-step string).",
        "Deal: {deal_name}. Amount: ${deal_amount}. Type: {deal_type}. Owner: {owner_email}. Rep data: {rep_data}. Close date: {close_date}.",
        [
          { field: "base_rate_pct", as: "base_rate_pct", type: "number" },
          {
            field: "accelerator_multiplier",
            as: "accelerator_multiplier",
            type: "number",
          },
          { field: "gross_commission", as: "gross_commission", type: "number" },
          { field: "split_details", as: "split_details", type: "string" },
          {
            field: "net_commission_per_rep",
            as: "net_commission",
            type: "number",
          },
          {
            field: "calculation_breakdown",
            as: "calc_breakdown",
            type: "string",
          },
        ],
      ),
      n("4", "hitApi", "HubSpot: Fetch deal owner and split data", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hubapi.com/crm/v3/objects/deals/{deal_id}?properties=dealname,amount,dealstage,closedate,deal_type,hubspot_owner_id,hs_all_owner_ids&associations=contacts",
          headers: [{ key: "Authorization", value: "Bearer {hubspot_token}" }],
          body: [],
          variables: [
            {
              name: "properties.hubspot_owner_id",
              customName: "primary_owner",
              type: "string",
            },
            {
              name: "properties.hs_all_owner_ids",
              customName: "all_owners",
              type: "string",
            },
            {
              name: "properties.closedate",
              customName: "close_date",
              type: "string",
            },
            {
              name: "properties.amount",
              customName: "verified_amount",
              type: "number",
            },
          ],
        },
      }),
      confidenceGate("5", "Gate: Commission calculation confidence", 680),
      sheetsNode(
        "6",
        "Sheets: Log commission to comp tracker",
        840,
        "CommissionTracker",
        '[["{case_id}","{deal_name}","{deal_amount}","{deal_type}","{owner_email}","{base_rate_pct}","{accelerator_multiplier}","{gross_commission}","{net_commission}","{split_details}","{close_date}","{started_at}"]]',
      ),
      approvalNode(
        "7",
        "Approval: Finance review (low confidence)",
        840,
        "Commission calculation needs review. Deal: {deal_name} for ${deal_amount}. Rep: {owner_email}. Gross commission: ${gross_commission}. Net per rep: ${net_commission}. Splits: {split_details}. Breakdown: {calc_breakdown}. AI confidence: {ai_confidence}%.",
        48,
        400,
      ),
      verifyNode(
        "8",
        "Verify: Commission logged in tracker",
        1000,
        "GET",
        "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/CommissionTracker!A:A?majorDimension=COLUMNS",
        [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Accept", value: "application/json" },
        ],
        [
          {
            name: "values[0][-1]",
            customName: "last_case_logged",
            type: "string",
          },
        ],
      ),
      conditionNode(
        "9",
        "Check: Commission log verified",
        1160,
        "verify_status",
        "EQUALS",
        "200",
      ),
      sheetsNode(
        "10",
        "Sheets: Audit trail entry",
        1320,
        "CommissionAudit",
        '[["{case_id}","{deal_id}","{owner_email}","{net_commission}","{ai_confidence}","{ai_evidence}","{calc_breakdown}","{started_at}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10", "output_1"),
    ],
    summary:
      "Triggers on closed-won deals, uses AI to calculate commission with accelerators and splits, auto-logs high-confidence calculations to the comp tracker, routes complex cases to finance, verifies logging, and creates audit trail entries.",
  },

  // ── 48. Elite: Vendor Onboarding Validator ──
  "Elite: Vendor Onboarding Validator": {
    nodes: [
      trigger("Webhook: New vendor onboarding form submitted", "webhook_form", {
        source: "VendorPortal",
        event: "vendor.form_submitted",
        howItStarts:
          "Fires when a new vendor completes the onboarding intake form with tax documents, compliance certifications, and banking details.",
        setup:
          "Connect the vendor portal webhook for form submissions. Payload includes company name, tax ID, W-9/W-8BEN documents, insurance certificates, banking info, and compliance questionnaire responses.",
        inputFields: [
          { name: "vendor_name", type: "string", example: "Acme Supplies LLC" },
          { name: "tax_id", type: "string", example: "12-3456789" },
          {
            name: "vendor_email",
            type: "string",
            example: "ap@acmesupplies.com",
          },
          {
            name: "documents_url",
            type: "string",
            example: "https://portal.example.com/vendors/acme/docs",
          },
          { name: "vendor_type", type: "string", example: "services" },
        ],
      }),
      caseStampNode(
        "2",
        "Stamp case: Vendor onboarding",
        200,
        "Vendor Onboarding Validator",
      ),
      aiNodeV2(
        "3",
        "OpenAI: Validate tax + compliance documents",
        360,
        "You are a vendor compliance specialist. Review the submitted vendor onboarding documents and validate: W-9/W-8BEN completeness and TIN format, insurance certificate validity and coverage minimums ($1M general liability), compliance questionnaire red flags, and OFAC/sanctions screening indicators. Return JSON with tax_doc_valid (boolean), insurance_valid (boolean), compliance_score (1-100), red_flags (comma-separated or 'none'), missing_documents (comma-separated or 'none'), vendor_risk_tier ('low','medium','high'), and recommended_action ('approve','request_additional_docs','reject').",
        "Vendor: {vendor_name}. Tax ID: {tax_id}. Type: {vendor_type}. Documents: {documents_url}. Questionnaire responses: {questionnaire_data}",
        [
          { field: "tax_doc_valid", as: "tax_doc_valid", type: "string" },
          { field: "insurance_valid", as: "insurance_valid", type: "string" },
          { field: "compliance_score", as: "compliance_score", type: "number" },
          { field: "red_flags", as: "red_flags", type: "string" },
          {
            field: "missing_documents",
            as: "missing_documents",
            type: "string",
          },
          { field: "vendor_risk_tier", as: "vendor_risk_tier", type: "string" },
          {
            field: "recommended_action",
            as: "recommended_action",
            type: "string",
          },
        ],
      ),
      n("4", "hitApi", "Compliance: Verify vendor documents and TIN", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.compliancecheck.com/v1/verify",
          headers: [
            { key: "Authorization", value: "Bearer {compliance_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "tax_id", value: "{tax_id}" },
            { key: "company_name", value: "{vendor_name}" },
            { key: "documents_url", value: "{documents_url}" },
          ],
          variables: [
            {
              name: "verification.tin_match",
              customName: "tin_verified",
              type: "string",
            },
            {
              name: "verification.sanctions_clear",
              customName: "sanctions_clear",
              type: "string",
            },
            {
              name: "verification.documents_complete",
              customName: "docs_complete",
              type: "string",
            },
          ],
        },
      }),
      confidenceGate("5", "Gate: Vendor validation confidence", 680),
      n("6", "hitApi", "QuickBooks: Create vendor record", 840, {
        hitApiDetails: {
          method: "POST",
          url: "https://quickbooks.api.intuit.com/v3/company/{realm_id}/vendor",
          headers: [
            { key: "Authorization", value: "Bearer {qb_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "DisplayName", value: "{vendor_name}" },
            { key: "PrimaryEmailAddr", value: '{"Address":"{vendor_email}"}' },
            { key: "TaxIdentifier", value: "{tax_id}" },
            {
              key: "Notes",
              value:
                "Auto-onboarded. Case: {case_id}. Risk tier: {vendor_risk_tier}. Compliance score: {compliance_score}.",
            },
          ],
          variables: [
            { name: "Vendor.Id", customName: "qb_vendor_id", type: "string" },
            {
              name: "Vendor.DisplayName",
              customName: "qb_vendor_name",
              type: "string",
            },
          ],
        },
      }),
      approvalNode(
        "7",
        "Approval: Procurement review (low confidence)",
        840,
        "Vendor onboarding needs review. Vendor: {vendor_name} ({vendor_email}). Tax doc valid: {tax_doc_valid}. Insurance valid: {insurance_valid}. Compliance score: {compliance_score}. Risk tier: {vendor_risk_tier}. Red flags: {red_flags}. Missing docs: {missing_documents}. AI confidence: {ai_confidence}%.",
        48,
        400,
      ),
      verifyNode(
        "8",
        "Verify: Vendor created in QuickBooks",
        1000,
        "GET",
        "https://quickbooks.api.intuit.com/v3/company/{realm_id}/vendor/{qb_vendor_id}",
        [
          { key: "Authorization", value: "Bearer {qb_token}" },
          { key: "Accept", value: "application/json" },
        ],
        [
          {
            name: "Vendor.Id",
            customName: "verified_vendor_id",
            type: "string",
          },
          {
            name: "Vendor.Active",
            customName: "vendor_active",
            type: "string",
          },
        ],
      ),
      conditionNode(
        "9",
        "Check: Vendor creation verified",
        1160,
        "verify_status",
        "EQUALS",
        "200",
      ),
      sheetsNode(
        "10",
        "Sheets: Log vendor onboarding outcome",
        1320,
        "VendorOnboardLog",
        '[["{case_id}","{vendor_name}","{tax_id}","{vendor_risk_tier}","{compliance_score}","{red_flags}","{missing_documents}","{ai_confidence}","{verified_vendor_id}","{started_at}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10", "output_1"),
    ],
    summary:
      "Validates new vendor onboarding submissions using AI for tax/compliance document review, auto-creates QuickBooks vendor records for high-confidence approvals, routes flagged vendors to procurement review, verifies creation, and logs to Sheets.",
  },

  // ── 49. Elite: AR Collection Sequence ──
  "Elite: AR Collection Sequence": {
    nodes: [
      trigger("Schedule: Daily AR aging review", "schedule", {
        schedule: "Every weekday at 9:00 AM UTC",
        howItStarts:
          "Runs daily to review accounts receivable aging and trigger collection actions for overdue invoices.",
        setup:
          "Configure daily schedule trigger. Ensure QuickBooks API access for aging reports. Set up email templates for friendly reminder (30 days), firm reminder (60 days), and final notice (90+ days).",
        inputFields: [],
      }),
      caseStampNode(
        "2",
        "Stamp case: AR collection run",
        200,
        "AR Collection Sequence",
      ),
      aiNodeV2(
        "3",
        "OpenAI: Prioritize overdue invoices + recommend actions",
        360,
        "You are an accounts receivable specialist. Analyze the aging report and prioritize collection actions. For each overdue invoice, determine: priority (1-5 where 1 is highest), collection_stage ('friendly_reminder','firm_follow_up','final_notice','collections_referral'), recommended_channel ('email','phone','both'), and personalized_message. Return JSON with total_overdue_amount, invoices_analyzed (count), high_priority_count, collection_actions (string summary of top actions to take), email_batch (stringified array of {customer_email, customer_name, invoice_number, amount, days_overdue, message}), and escalation_needed (boolean).",
        "AR aging report data: {aging_data}. Customer payment history context: {payment_history}.",
        [
          {
            field: "total_overdue_amount",
            as: "total_overdue_amount",
            type: "number",
          },
          {
            field: "invoices_analyzed",
            as: "invoices_analyzed",
            type: "number",
          },
          {
            field: "high_priority_count",
            as: "high_priority_count",
            type: "number",
          },
          {
            field: "collection_actions",
            as: "collection_actions",
            type: "string",
          },
          { field: "email_batch", as: "email_batch", type: "string" },
          {
            field: "escalation_needed",
            as: "escalation_needed",
            type: "string",
          },
        ],
      ),
      n("4", "hitApi", "QuickBooks: Fetch AR aging summary", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://quickbooks.api.intuit.com/v3/company/{realm_id}/reports/AgedReceivables?date_macro=Today",
          headers: [
            { key: "Authorization", value: "Bearer {qb_token}" },
            { key: "Accept", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "Rows", customName: "aging_data", type: "string" },
            {
              name: "Header.ReportName",
              customName: "report_name",
              type: "string",
            },
          ],
        },
      }),
      confidenceGate("5", "Gate: Collection prioritization confidence", 680),
      emailNode(
        "6",
        "Email: Send payment reminders batch",
        840,
        "{top_customer_email}",
        "Payment Reminder: Invoice #{top_invoice_number} — ${top_amount} overdue",
        "Dear {top_customer_name},\\n\\nThis is a reminder that invoice #{top_invoice_number} for ${top_amount} is now {top_days_overdue} days past due.\\n\\nPlease submit payment at your earliest convenience. If you have already sent payment, please disregard this notice.\\n\\nPayment portal: https://pay.yourcompany.com/{top_invoice_number}\\n\\nQuestions? Reply to this email.\\n\\nBest regards,\\nAccounts Receivable",
      ),
      approvalNode(
        "7",
        "Approval: Finance review (low confidence)",
        840,
        "AR collection run needs review. Total overdue: ${total_overdue_amount}. Invoices analyzed: {invoices_analyzed}. High priority: {high_priority_count}. Escalation needed: {escalation_needed}. Actions: {collection_actions}. AI confidence: {ai_confidence}%.",
        24,
        400,
      ),
      verifyNode(
        "8",
        "Verify: Reminder emails sent",
        1000,
        "GET",
        "https://api.postmarkapp.com/messages/outbound?count=10&offset=0",
        [
          { key: "X-Postmark-Server-Token", value: "{postmark_token}" },
          { key: "Accept", value: "application/json" },
        ],
        [
          {
            name: "TotalCount",
            customName: "emails_sent_count",
            type: "number",
          },
          {
            name: "Messages[0].Status",
            customName: "last_email_status",
            type: "string",
          },
        ],
      ),
      conditionNode(
        "9",
        "Check: Email delivery verified",
        1160,
        "verify_status",
        "EQUALS",
        "200",
      ),
      sheetsNode(
        "10",
        "Sheets: Log collection run results",
        1320,
        "ARCollectionLog",
        '[["{case_id}","{total_overdue_amount}","{invoices_analyzed}","{high_priority_count}","{escalation_needed}","{collection_actions}","{ai_confidence}","{emails_sent_count}","{started_at}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10", "output_1"),
    ],
    summary:
      "Daily schedule fetches AR aging from QuickBooks, uses AI to prioritize overdue invoices and compose personalized collection messages, auto-sends reminders for high-confidence cases, routes complex situations to finance review, verifies delivery, and logs results.",
  },

  // ── 50. Elite: Contract Renewal Prep ──
  "Elite: Contract Renewal Prep": {
    nodes: [
      trigger("Schedule: Weekly contract renewal scan", "schedule", {
        schedule: "Every Monday at 7:00 AM UTC",
        howItStarts:
          "Runs weekly to identify contracts expiring in the next 90 days and prepare renewal briefs for account managers.",
        setup:
          "Configure weekly schedule trigger. Ensure HubSpot API access for contract and usage data. Renewal prep briefs will be emailed to assigned account managers.",
        inputFields: [],
      }),
      caseStampNode(
        "2",
        "Stamp case: Renewal prep scan",
        200,
        "Contract Renewal Prep",
      ),
      aiNodeV2(
        "3",
        "OpenAI: Assess renewal risk + recommend terms",
        360,
        "You are a customer success strategist specializing in renewals. Analyze contract and usage data to assess renewal risk and recommend terms. Consider: product usage trends, support ticket volume, NPS scores, expansion opportunities, competitive threats, and pricing sensitivity. Return JSON with contracts_expiring (count), high_risk_count, medium_risk_count, low_risk_count, renewal_briefs (string summary of top 5 contracts with risk assessment and recommended terms), total_arr_at_risk, recommended_discounts (string summary), and upsell_opportunities (string listing expansion plays).",
        "Contracts expiring in 90 days from HubSpot: {contracts_data}. Usage metrics: {usage_data}. Support history: {support_data}.",
        [
          {
            field: "contracts_expiring",
            as: "contracts_expiring",
            type: "number",
          },
          { field: "high_risk_count", as: "high_risk_count", type: "number" },
          {
            field: "total_arr_at_risk",
            as: "total_arr_at_risk",
            type: "number",
          },
          { field: "renewal_briefs", as: "renewal_briefs", type: "string" },
          {
            field: "recommended_discounts",
            as: "recommended_discounts",
            type: "string",
          },
          {
            field: "upsell_opportunities",
            as: "upsell_opportunities",
            type: "string",
          },
        ],
      ),
      n("4", "hitApi", "HubSpot: Fetch expiring contracts + usage data", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/deals/search",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "filterGroups",
              value:
                '[{"filters":[{"propertyName":"contract_end_date","operator":"LTE","value":"{ninety_days_from_now}"},{"propertyName":"contract_end_date","operator":"GTE","value":"{today}"},{"propertyName":"dealstage","operator":"EQ","value":"active"}]}]',
            },
            {
              key: "properties",
              value:
                '["dealname","amount","contract_end_date","hubspot_owner_id","company","product_usage_score","nps_score","support_tickets_count"]',
            },
            { key: "limit", value: "100" },
          ],
          variables: [
            { name: "total", customName: "total_expiring", type: "number" },
            { name: "results", customName: "contracts_data", type: "string" },
          ],
        },
      }),
      confidenceGate("5", "Gate: Renewal risk assessment confidence", 680),
      emailNode(
        "6",
        "Email: Send renewal prep briefs to AMs",
        840,
        "{account_manager_email}",
        "Renewal Prep Brief — {contracts_expiring} contracts expiring in 90 days",
        "Hi Team,\\n\\nHere is your weekly renewal prep brief. Case ID: {case_id}\\n\\n== Summary ==\\nContracts expiring: {contracts_expiring}\\nHigh risk: {high_risk_count}\\nTotal ARR at risk: ${total_arr_at_risk}\\n\\n== Top Renewal Briefs ==\\n{renewal_briefs}\\n\\n== Upsell Opportunities ==\\n{upsell_opportunities}\\n\\n== Recommended Discounts ==\\n{recommended_discounts}\\n\\nPlease review and begin outreach for high-risk accounts this week.\\n\\nBest,\\nRevOps Automation",
      ),
      approvalNode(
        "7",
        "Approval: Account manager review (low confidence)",
        840,
        "Renewal prep brief needs AM review. Contracts expiring: {contracts_expiring}. High risk: {high_risk_count}. ARR at risk: ${total_arr_at_risk}. AI confidence: {ai_confidence}%. Please verify risk assessments and recommended terms before distributing.",
        48,
        400,
      ),
      verifyNode(
        "8",
        "Verify: Prep brief emails sent",
        1000,
        "GET",
        "https://api.postmarkapp.com/messages/outbound?recipient={account_manager_email}&count=1",
        [
          { key: "X-Postmark-Server-Token", value: "{postmark_token}" },
          { key: "Accept", value: "application/json" },
        ],
        [
          {
            name: "Messages[0].Status",
            customName: "brief_email_status",
            type: "string",
          },
          {
            name: "Messages[0].MessageID",
            customName: "brief_message_id",
            type: "string",
          },
        ],
      ),
      conditionNode(
        "9",
        "Check: Brief delivery verified",
        1160,
        "verify_status",
        "EQUALS",
        "200",
      ),
      sheetsNode(
        "10",
        "Sheets: Log renewal prep run",
        1320,
        "RenewalPrepLog",
        '[["{case_id}","{contracts_expiring}","{high_risk_count}","{total_arr_at_risk}","{upsell_opportunities}","{ai_confidence}","{brief_email_status}","{started_at}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("7", "6", "output_1"),
      e("6", "8"),
      e("8", "9"),
      e("9", "10", "output_1"),
    ],
    summary:
      "Weekly scan identifies contracts expiring in 90 days from HubSpot, uses AI to assess renewal risk and recommend terms/upsell plays, emails prep briefs to account managers, routes uncertain assessments for review, verifies delivery, and logs to Sheets.",
  },
};
